import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { AlertCircle, XCircle, CheckCircle, X, Wifi, Globe, Lock, Server, Code, Monitor, Settings, Loader2 } from 'lucide-react';
import EncryptedStorage from 'core/utils/constants/encryptedStorage';

const TroubleshootingWrapper = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [allResults, setAllResults] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [resolvingIssue, setResolvingIssue] = useState(null);

  useEffect(() => {
    // Listen for keyboard shortcut Ctrl+Shift+V
    const handleKeyPress = (event) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'V') {
        event.preventDefault();
        openTroubleshootingModal();
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  const openTroubleshootingModal = () => {
    setIsModalOpen(true);
    setIsRunning(true);
    runDiagnostics();
  };

  const runDiagnostics = async () => {
    const results = [];

    // Run synchronous checks first (these don't require network)
    const browserInfo = detectBrowser();
    const osInfo = detectOS();
    const featureInfo = checkFeatureCompatibility();
    const storageInfo = checkStorage();
    const sslInfo = checkSSL();

    // Check network status first (synchronous)
    const networkInfo = checkNetworkStatus();

    // Run async checks with proper error handling
    let networkLatencyInfo = null;
    let packetLossInfo = null;
    let corsInfo = null;
    let apiInfo = null;

    // Only run network-dependent checks if we appear to be online
    if (navigator.onLine) {
      try {
        networkLatencyInfo = await checkNetworkLatency();
      } catch (err) {
        networkLatencyInfo = {
          category: 'Network',
          icon: Wifi,
          status: 'error',
          title: 'Network Latency Check',
          message: 'Unable to measure network latency - Connection may be unstable',
          details: { error: err.message },
          solutions: [
            'Check your internet connection',
            'Restart your router',
            'Try disabling VPN if enabled',
            'Check firewall settings'
          ],
          autoResolvable: false
        };
      }

      try {
        packetLossInfo = await checkPacketLoss();
      } catch (err) {
        packetLossInfo = {
          category: 'Network',
          icon: Wifi,
          status: 'error',
          title: 'Packet Loss Check',
          message: 'Unable to measure packet loss - Connection may be unstable',
          details: { error: err.message },
          solutions: [
            'Check your internet connection',
            'Restart your router',
            'Try disabling VPN if enabled',
            'Check firewall settings'
          ],
          autoResolvable: false
        };
      }

      try {
        corsInfo = await checkCORS();
      } catch (err) {
        corsInfo = {
          category: 'CORS',
          icon: Globe,
          status: 'error',
          title: 'CORS Check',
          message: 'CORS check failed - Network may be blocking requests',
          details: { error: err.message },
          solutions: [
            'Check network/firewall settings',
            'Verify you have internet access',
            'Contact network administrator'
          ],
          autoResolvable: false
        };
      }

      try {
        apiInfo = await checkAPIHealth();
      } catch (err) {
        apiInfo = {
          category: 'API',
          icon: Server,
          status: 'error',
          title: 'API Health Check',
          message: 'API check failed - No internet connection or server unreachable',
          details: { error: err.message },
          solutions: [
            'Check your internet connection',
            'Verify network is not blocking API requests',
            'Check if API server is running',
            'Try again in a few moments'
          ],
          autoResolvable: false
        };
      }
    } else {
      // If offline, create error entries for network-dependent checks
      networkLatencyInfo = {
        category: 'Network',
        icon: Wifi,
        status: 'error',
        title: 'Network Latency',
        message: 'Cannot measure latency - No internet connection',
        details: { skipped: true, reason: 'offline' },
        solutions: ['Reconnect to the internet to perform this check'],
        autoResolvable: false
      };

      packetLossInfo = {
        category: 'Network',
        icon: Wifi,
        status: 'error',
        title: 'Packet Loss Check',
        message: 'Cannot measure packet loss - No internet connection',
        details: { skipped: true, reason: 'offline' },
        solutions: ['Reconnect to the internet to perform this check'],
        autoResolvable: false
      };

      corsInfo = {
        category: 'CORS',
        icon: Globe,
        status: 'warning',
        title: 'CORS Check',
        message: 'Cannot check CORS - No internet connection',
        details: { skipped: true, reason: 'offline' },
        solutions: ['Restore internet connection to perform this check'],
        autoResolvable: false
      };

      apiInfo = {
        category: 'API',
        icon: Server,
        status: 'warning',
        title: 'API Health Check',
        message: 'Cannot check API - No internet connection',
        details: { skipped: true, reason: 'offline' },
        solutions: ['Restore internet connection to perform this check'],
        autoResolvable: false
      };
    }

    // Collect all results (including successful checks)
    const diagnosticResults = [
      browserInfo || {
        category: 'Browser',
        icon: Globe,
        status: 'success',
        title: 'Browser Version Check',
        message: 'Browser is up to date',
        details: { browser: getBrowserInfo().browser, version: getBrowserInfo().version },
        solutions: []
      },
      osInfo || {
        category: 'Operating System',
        icon: Monitor,
        status: 'success',
        title: 'OS Compatibility',
        message: 'Operating system is compatible',
        details: { os: getOSInfo().os, version: getOSInfo().version },
        solutions: []
      },
      networkInfo || {
        category: 'Network',
        icon: Wifi,
        status: 'success',
        title: 'Network Connection',
        message: 'Internet connection is active',
        details: { online: true },
        solutions: []
      },
      networkLatencyInfo || {
        category: 'Network',
        icon: Wifi,
        status: 'success',
        title: 'Network Latency',
        message: 'Network latency is normal',
        details: { status: 'good' },
        solutions: []
      },
      packetLossInfo || {
        category: 'Network',
        icon: Wifi,
        status: 'success',
        title: 'Packet Loss Check',
        message: 'No packet loss detected',
        details: { packetLoss: '0%' },
        solutions: []
      },
      featureInfo || {
        category: 'Features',
        icon: Code,
        status: 'success',
        title: 'JavaScript Feature Support',
        message: 'All required features supported',
        details: { allSupported: true },
        solutions: []
      },
      storageInfo || {
        category: 'Storage',
        icon: Settings,
        status: 'success',
        title: 'Cookies & Local Storage',
        message: 'Cookies and storage enabled',
        details: { cookiesEnabled: true, localStorageWorks: true },
        solutions: []
      },
      sslInfo || {
        category: 'Security',
        icon: Lock,
        status: 'success',
        title: 'SSL/HTTPS Security',
        message: 'Secure HTTPS connection',
        details: { protocol: window.location.protocol, isHTTPS: true },
        solutions: []
      },
      corsInfo || {
        category: 'CORS',
        icon: Globe,
        status: 'success',
        title: 'CORS & Cross-Origin Requests',
        message: 'No CORS issues detected',
        details: { status: 'ok' },
        solutions: []
      },
      apiInfo || {
        category: 'API',
        icon: Server,
        status: 'success',
        title: 'API Health Check',
        message: 'API is reachable and healthy',
        details: { statusCode: 200 },
        solutions: []
      }
    ];

    setAllResults(diagnosticResults);
    setIsRunning(false);
  };

  const getBrowserInfo = () => {
    const ua = navigator.userAgent;
    let browser = 'Unknown';
    let version = 'Unknown';

    if (ua.includes('Firefox/')) {
      browser = 'Firefox';
      version = ua.match(/Firefox\/(\d+)/)?.[1];
    } else if (ua.includes('Chrome/') && !ua.includes('Edg')) {
      browser = 'Chrome';
      version = ua.match(/Chrome\/(\d+)/)?.[1];
    } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
      browser = 'Safari';
      version = ua.match(/Version\/(\d+)/)?.[1];
    } else if (ua.includes('Edg/')) {
      browser = 'Edge';
      version = ua.match(/Edg\/(\d+)/)?.[1];
    }

    return { browser, version };
  };

  const getOSInfo = () => {
    const ua = navigator.userAgent;
    let os = 'Unknown';
    let version = '';

    if (ua.includes('Windows NT 10.0')) {
      os = 'Windows 10/11';
      version = '10+';
    } else if (ua.includes('Windows NT 6.3')) {
      os = 'Windows 8.1';
      version = '8.1';
    } else if (ua.includes('Windows NT 6.1')) {
      os = 'Windows 7';
      version = '7';
    } else if (ua.includes('Mac OS X')) {
      os = 'macOS';
      version = ua.match(/Mac OS X (\d+_\d+)/)?.[1]?.replace('_', '.') || '';
    } else if (ua.includes('Linux')) {
      os = 'Linux';
    } else if (ua.includes('Android')) {
      os = 'Android';
      version = ua.match(/Android (\d+)/)?.[1] || '';
    } else if (ua.includes('iOS') || ua.includes('iPhone')) {
      os = 'iOS';
      version = ua.match(/OS (\d+_\d+)/)?.[1]?.replace('_', '.') || '';
    }

    return { os, version };
  };

  const detectBrowser = () => {
    const { browser, version } = getBrowserInfo();
    const versionNum = parseInt(version);
    const minVersions = { Chrome: 90, Firefox: 88, Safari: 14, Edge: 90 };

    if (minVersions[browser] && versionNum < minVersions[browser]) {
      return {
        category: 'Browser',
        icon: Globe,
        status: 'error',
        title: 'Browser Version Check',
        message: `${browser} ${version} is outdated. Minimum recommended: ${minVersions[browser]}+`,
        details: { browser, version },
        solutions: [
          `Update ${browser} to the latest version`,
          'Visit your browser\'s official website to download updates',
          'Enable automatic updates in browser settings'
        ],
        autoResolvable: false
      };
    }

    return null;
  };

  const detectOS = () => {
    const { os, version } = getOSInfo();
    let status = 'success';
    let message = '';
    let solutions = [];

    if (os === 'Windows 8.1') {
      status = 'warning';
      message = `${os} ${version} - May have compatibility issues`;
      solutions = ['Consider upgrading to Windows 10 or 11 for better compatibility'];
    } else if (os === 'Windows 7') {
      status = 'error';
      message = `${os} ${version} - No longer supported`;
      solutions = ['Upgrade to Windows 10 or 11', 'Windows 7 is no longer supported'];
    }

    return status === 'success' ? null : {
      category: 'Operating System',
      icon: Monitor,
      status,
      title: 'OS Compatibility',
      message,
      details: { os, version },
      solutions,
      autoResolvable: false
    };
  };

  const checkNetworkStatus = () => {
    const isOnline = navigator.onLine;

    if (!isOnline) {
      return {
        category: 'Network',
        icon: Wifi,
        status: 'error',
        title: 'Network Connection',
        message: 'No internet connection detected',
        details: { online: false },
        solutions: [
          'Check WiFi/Ethernet connection',
          'Restart your router',
          'Verify network settings',
          'Check if airplane mode is disabled',
          'Try connecting to a different network'
        ],
        autoResolvable: false
      };
    }

    return null;
  };

  const checkNetworkLatency = async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
      const start = Date.now();
      await fetch('https://www.google.com/favicon.ico', {
        mode: 'no-cors',
        cache: 'no-store',
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      const latency = Date.now() - start;

      if (latency > 3000) {
        return {
          category: 'Network',
          icon: Wifi,
          status: 'warning',
          title: 'Network Latency',
          message: `Slow connection detected (${latency}ms)`,
          details: { latency },
          solutions: [
            'Check your internet speed',
            'Try connecting to a different network',
            'Close bandwidth-heavy applications',
            'Contact your ISP if problem persists'
          ],
          autoResolvable: true
        };
      }

      return null;
    } catch (err) {
      clearTimeout(timeoutId);

      if (err.name === 'AbortError') {
        return {
          category: 'Network',
          icon: Wifi,
          status: 'error',
          title: 'Network Timeout',
          message: 'Network request timed out - Connection is very slow or unstable',
          details: { error: 'timeout' },
          solutions: [
            'Check your internet connection',
            'Try restarting your router',
            'Check for network congestion',
            'Contact your ISP'
          ],
          autoResolvable: false
        };
      }

      return {
        category: 'Network',
        icon: Wifi,
        status: 'error',
        title: 'Network Request Failed',
        message: 'Network request failed - Firewall or proxy may be blocking',
        details: { error: err.message },
        solutions: [
          'Check firewall settings',
          'Verify proxy configuration',
          'Contact network administrator',
          'Try disabling VPN if enabled'
        ],
        autoResolvable: false
      };
    }
  };

  const checkPacketLoss = async () => {
    const totalPackets = 10;
    const timeout = 3000;
    let successfulPackets = 0;
    let failedPackets = 0;
    let timeouts = 0;
    const latencies = [];

    const requests = Array.from({ length: totalPackets }, async (_, i) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      try {
        const start = Date.now();
        await fetch('https://www.google.com/favicon.ico', {
          mode: 'no-cors',
          cache: 'no-store',
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        const latency = Date.now() - start;
        latencies.push(latency);
        successfulPackets++;
        return { success: true, latency };
      } catch (err) {
        clearTimeout(timeoutId);
        if (err.name === 'AbortError') {
          timeouts++;
        }
        failedPackets++;
        return { success: false, error: err.name };
      }
    });

    await Promise.all(requests);

    const packetLossPercentage = (failedPackets / totalPackets) * 100;
    const avgLatency = latencies.length > 0
      ? Math.round(latencies.reduce((a, b) => a + b, 0) / latencies.length)
      : 0;
    const jitter = latencies.length > 1
      ? Math.round(Math.max(...latencies) - Math.min(...latencies))
      : 0;

    if (packetLossPercentage >= 20) {
      return {
        category: 'Network',
        icon: Wifi,
        status: 'error',
        title: 'Packet Loss Detected',
        message: `Severe packet loss: ${packetLossPercentage.toFixed(1)}% of packets lost`,
        details: {
          packetLoss: `${packetLossPercentage.toFixed(1)}%`,
          packetsLost: failedPackets,
          packetsSent: totalPackets,
          timeouts,
          avgLatency: `${avgLatency}ms`,
          jitter: `${jitter}ms`
        },
        solutions: [
          'Check WiFi signal strength or Ethernet cable connection',
          'Move closer to WiFi router or use wired connection',
          'Restart your router and modem',
          'Check for interference from other devices',
          'Contact your ISP - line quality may be degraded',
          'Update router firmware',
          'Try a different DNS server (e.g., 8.8.8.8, 1.1.1.1)'
        ],
        autoResolvable: false
      };
    } else if (packetLossPercentage >= 5) {
      return {
        category: 'Network',
        icon: Wifi,
        status: 'warning',
        title: 'Packet Loss Detected',
        message: `Moderate packet loss: ${packetLossPercentage.toFixed(1)}% of packets lost`,
        details: {
          packetLoss: `${packetLossPercentage.toFixed(1)}%`,
          packetsLost: failedPackets,
          packetsSent: totalPackets,
          timeouts,
          avgLatency: `${avgLatency}ms`,
          jitter: `${jitter}ms`
        },
        solutions: [
          'Check WiFi signal strength',
          'Reduce distance to router or remove obstacles',
          'Restart your router',
          'Check for network congestion',
          'Consider using wired connection',
          'Contact ISP if problem persists'
        ],
        autoResolvable: true
      };
    }

    return null;
  };

  const checkFeatureCompatibility = () => {
    const features = {
      fetch: typeof fetch !== 'undefined',
      promise: typeof Promise !== 'undefined',
      EncryptedStorage: typeof EncryptedStorage !== 'undefined',
      sessionStorage: typeof sessionStorage !== 'undefined',
      webWorker: typeof Worker !== 'undefined',
      webSocket: typeof WebSocket !== 'undefined',
      es6Arrow: (() => { try { eval('()=>{}'); return true; } catch (e) { return false; } })(),
      asyncAwait: (() => { try { eval('async ()=>{}'); return true; } catch (e) { return false; } })()
    };

    const missing = Object.entries(features).filter(([k, v]) => !v).map(([k]) => k);

    if (missing.length > 0) {
      return {
        category: 'Features',
        icon: Code,
        status: 'error',
        title: 'JavaScript Feature Support',
        message: `Missing features: ${missing.join(', ')}`,
        details: features,
        solutions: [
          'Update your browser to the latest version',
          'Use a modern browser (Chrome, Firefox, Edge, Safari)',
          'Enable JavaScript in browser settings',
          'Clear browser cache and restart browser'
        ],
        autoResolvable: false
      };
    }

    return null;
  };

  const checkStorage = () => {
    let cookiesEnabled = false;
    let localStorageWorks = false;

    try {
      document.cookie = 'testcookie=1';
      cookiesEnabled = document.cookie.indexOf('testcookie') !== -1;
      document.cookie = 'testcookie=1; expires=Thu, 01-Jan-1970 00:00:01 GMT';
    } catch (e) {
      cookiesEnabled = false;
    }

    try {
      EncryptedStorage.setItem('test', 'test');
      EncryptedStorage.removeItem('test');
      localStorageWorks = true;
    } catch (e) {
      localStorageWorks = false;
    }

    if (!cookiesEnabled || !localStorageWorks) {
      return {
        category: 'Storage',
        icon: Settings,
        status: 'error',
        title: 'Cookies & Local Storage',
        message: `Storage issues: ${!cookiesEnabled ? 'Cookies disabled' : ''} ${!localStorageWorks ? 'Storage blocked' : ''}`.trim(),
        details: { cookiesEnabled, localStorageWorks },
        solutions: [
          'Enable cookies in browser settings',
          'Exit private/incognito mode',
          'Disable browser extensions that block storage',
          'Clear browser cache and cookies',
          'Check browser privacy settings'
        ],
        autoResolvable: true
      };
    }

    return null;
  };

  const checkSSL = () => {
    const isHTTPS = window.location.protocol === 'https:';
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const hasMixedContent = !isHTTPS && !isLocalhost;

    if (hasMixedContent) {
      return {
        category: 'Security',
        icon: Lock,
        status: 'error',
        title: 'SSL/HTTPS Security',
        message: 'Using HTTP - Security features blocked',
        details: { protocol: window.location.protocol, isHTTPS },
        solutions: [
          'Use HTTPS version of the site',
          'Contact site administrator to enable SSL',
          'Check URL for https:// prefix',
          'Bookmark the HTTPS version'
        ],
        autoResolvable: false
      };
    } else if (!isHTTPS && isLocalhost) {
      return {
        category: 'Security',
        icon: Lock,
        status: 'warning',
        title: 'SSL/HTTPS Security',
        message: 'Using HTTP on localhost - Some features may be limited',
        details: { protocol: window.location.protocol, isHTTPS, isLocalhost },
        solutions: ['Some browser features require HTTPS in production'],
        autoResolvable: false
      };
    }

    return null;
  };

  const checkCORS = async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
      const response = await fetch('https://httpbin.org/get', {
        method: 'GET',
        mode: 'cors',
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        return {
          category: 'CORS',
          icon: Globe,
          status: 'error',
          title: 'CORS & Cross-Origin Requests',
          message: 'CORS request failed',
          details: { status: response.status, referrerPolicy: document.referrerPolicy || 'default' },
          solutions: [
            'Ensure API server has correct CORS headers',
            'Check Access-Control-Allow-Origin settings',
            'Use a proxy or backend to handle requests',
            'Contact API provider for CORS support'
          ],
          autoResolvable: false
        };
      }

      return null;
    } catch (err) {
      clearTimeout(timeoutId);

      if (err.name === 'AbortError') {
        return {
          category: 'CORS',
          icon: Globe,
          status: 'warning',
          title: 'CORS Check Timeout',
          message: 'CORS check timed out - Network may be slow',
          details: { error: 'timeout' },
          solutions: ['Check network connection', 'Try again later'],
          autoResolvable: false
        };
      }

      if (err.message.includes('CORS') || err.message.includes('Failed to fetch')) {
        return {
          category: 'CORS',
          icon: Globe,
          status: 'error',
          title: 'CORS & Cross-Origin Requests',
          message: 'CORS error detected',
          details: { error: err.message, referrerPolicy: document.referrerPolicy || 'default' },
          solutions: [
            'Ensure API server has correct CORS headers',
            'Check Access-Control-Allow-Origin settings',
            'Verify network is not blocking requests',
            'Use a proxy or backend to handle requests'
          ],
          autoResolvable: false
        };
      }

      throw err;
    }
  };

  const checkAPIHealth = async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
      const response = await fetch('https://httpbin.org/status/200', {
        cache: 'no-store',
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      const statusCode = response.status;

      if (statusCode >= 500) {
        return {
          category: 'API',
          icon: Server,
          status: 'error',
          title: 'API Health Check',
          message: `Server error detected (${statusCode})`,
          details: { statusCode },
          solutions: [
            'Server may be down - try again later',
            'Contact support team',
            'Check API status page',
            'Wait a few minutes and refresh'
          ],
          autoResolvable: false
        };
      } else if (statusCode >= 400) {
        return {
          category: 'API',
          icon: Server,
          status: 'warning',
          title: 'API Health Check',
          message: `Client error detected (${statusCode})`,
          details: { statusCode },
          solutions: [
            'Check API authentication',
            'Verify request parameters',
            'Clear browser cache and cookies'
          ],
          autoResolvable: false
        };
      }

      return null;
    } catch (err) {
      clearTimeout(timeoutId);

      if (err.name === 'AbortError') {
        return {
          category: 'API',
          icon: Server,
          status: 'error',
          title: 'API Health Check',
          message: 'API request timed out - Server unreachable or network too slow',
          details: { error: 'timeout' },
          solutions: [
            'Check your internet connection',
            'Verify API server is running',
            'Check network/firewall settings',
            'Try again in a few moments'
          ],
          autoResolvable: false
        };
      }

      return {
        category: 'API',
        icon: Server,
        status: 'error',
        title: 'API Health Check',
        message: 'API request failed - Server unreachable',
        details: { error: err.message },
        solutions: [
          'Check your internet connection',
          'Verify API server is running',
          'Check network/firewall settings',
          'Try again in a few moments'
        ],
        autoResolvable: false
      };
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'border-green-300 bg-green-50';
      case 'warning': return 'border-yellow-300 bg-yellow-50';
      case 'error': return 'border-red-300 bg-red-50';
      default: return 'border-gray-300 bg-gray-50';
    }
  };

  const getStatusTextColor = (status) => {
    switch (status) {
      case 'success': return 'text-green-800';
      case 'warning': return 'text-yellow-800';
      case 'error': return 'text-red-800';
      default: return 'text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return CheckCircle;
      case 'warning': return AlertCircle;
      case 'error': return XCircle;
      default: return AlertCircle;
    }
  };

  const resolveIssue = async (issue, index) => {
    setResolvingIssue(index);

    try {
      if (issue.category === 'Network' && issue.title === 'Network Latency') {
        await new Promise(resolve => setTimeout(resolve, 1000));
        setAllResults(prev => prev.map((item, i) =>
          i === index ? { ...item, status: 'success', message: 'Issue resolved' } : item
        ));
      } else if (issue.category === 'Network' && issue.title === 'Packet Loss Detected') {
        await new Promise(resolve => setTimeout(resolve, 1000));
        alert('Please restart your router and try changing DNS servers (e.g., 8.8.8.8 or 1.1.1.1) for better stability.');
        const newCheck = await checkPacketLoss();
        if (!newCheck) {
          setAllResults(prev => prev.map((item, i) =>
            i === index ? { ...item, status: 'success', message: 'No packet loss detected' } : item
          ));
        }
      } else if (issue.category === 'Storage') {
        try {
          EncryptedStorage.clear();
          sessionStorage.clear();
          await new Promise(resolve => setTimeout(resolve, 500));
          const newCheck = checkStorage();
          if (!newCheck) {
            setAllResults(prev => prev.map((item, i) =>
              i === index ? { ...item, status: 'success', message: 'Cookies and storage enabled' } : item
            ));
          }
        } catch (e) {
          alert('Unable to automatically resolve storage issue. Please follow manual steps.');
        }
      } else {
        alert('This issue requires manual intervention. Please follow the recommended solutions.');
      }
    } catch (error) {
      alert('Failed to resolve issue automatically. Please try manual solutions.');
    } finally {
      setResolvingIssue(null);
    }
  };

  const isAutoResolvable = (issue) => {
    return issue.autoResolvable === true;
  };

  const modalStyles = {
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      zIndex: 9999
    },
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      maxWidth: '900px',
      width: '90%',
      maxHeight: '90vh',
      padding: '0',
      border: 'none',
      borderRadius: '12px',
      overflow: 'hidden'
    }
  };

  const errorCount = allResults.filter(i => i.status === 'error').length;
  const warningCount = allResults.filter(i => i.status === 'warning').length;
  const successCount = allResults.filter(i => i.status === 'success').length;

  return (
    <>
      {children}

      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        style={modalStyles}
        contentLabel="Troubleshooting Diagnostics"
      >
        <div className="bg-white">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white p-6">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold mb-2">System Diagnostics</h2>
                <p className="text-blue-100">
                  Press <kbd className="px-2 py-1 bg-white bg-opacity-20 rounded">Ctrl+Shift+V</kbd> to run diagnostics anytime
                </p>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Stats */}
            {!isRunning && (
              <div className="flex gap-4 mt-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-green-500 bg-opacity-30 rounded-lg">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-semibold">{successCount} Success</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-yellow-500 bg-opacity-30 rounded-lg">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-semibold">{warningCount} Warnings</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-red-500 bg-opacity-30 rounded-lg">
                  <XCircle className="w-5 h-5" />
                  <span className="font-semibold">{errorCount} Errors</span>
                </div>
              </div>
            )}
          </div>

          {/* Loading State */}
          {isRunning && (
            <div className="flex flex-col items-center justify-center p-12">
              <Loader2 className="w-16 h-16 text-blue-600 animate-spin mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">Running Diagnostics...</h3>
              <p className="text-gray-600">Please wait while we check your system</p>
            </div>
          )}

          {/* Results */}
          {!isRunning && (
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <div className="space-y-4">
                {allResults.map((result, idx) => {
                  const StatusIcon = getStatusIcon(result.status);
                  const CategoryIcon = result.icon;

                  return (
                    <div
                      key={idx}
                      className={`border-2 rounded-lg overflow-hidden ${getStatusColor(result.status)}`}
                    >
                      <div className="p-5">
                        <div className="flex items-start gap-4">
                          <CategoryIcon className={`w-8 h-8 flex-shrink-0 ${getStatusTextColor(result.status)}`} />
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className={`text-lg font-bold ${getStatusTextColor(result.status)}`}>
                                {result.title}
                              </h3>
                              <StatusIcon className={`w-5 h-5 ${getStatusTextColor(result.status)}`} />
                            </div>

                            <p className={`font-medium mb-3 ${getStatusTextColor(result.status)}`}>
                              {result.message}
                            </p>

                            {/* Technical Details */}
                            <details className="mb-3">
                              <summary className="cursor-pointer font-semibold text-gray-800 text-sm mb-2 hover:text-gray-600">
                                View Technical Details
                              </summary>
                              <div className="p-3 bg-white rounded-lg border border-gray-200">
                                <pre className="text-xs text-gray-700 overflow-auto">
                                  {JSON.stringify(result.details, null, 2)}
                                </pre>
                              </div>
                            </details>

                            {/* Recommended Solutions - Only show if there are solutions */}
                            {result.solutions && result.solutions.length > 0 && (
                              <div className="p-4 bg-white rounded-lg border border-gray-200">
                                <div className="flex justify-between items-center mb-3">
                                  <h4 className="font-bold text-gray-800 flex items-center gap-2">
                                    <Settings className="w-4 h-4" />
                                    Recommended Solutions:
                                  </h4>
                                  {result.status !== 'success' && (
                                    <button
                                      onClick={() => resolveIssue(result, idx)}
                                      disabled={!isAutoResolvable(result) || resolvingIssue === idx}
                                      className={`px-4 py-2 rounded-lg font-medium text-sm transition ${isAutoResolvable(result)
                                          ? 'bg-green-600 text-white hover:bg-green-700 disabled:bg-green-400'
                                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        }`}
                                      title={!isAutoResolvable(result) ? 'This issue requires manual resolution' : 'Click to auto-resolve'}
                                    >
                                      {resolvingIssue === idx ? 'Resolving...' : isAutoResolvable(result) ? 'Auto Resolve' : 'Manual Fix Required'}
                                    </button>
                                  )}
                                </div>
                                <ul className="space-y-2">
                                  {result.solutions.map((solution, sidx) => (
                                    <li key={sidx} className="flex items-baseline gap-2 text-gray-700">
                                      <span className="text-lg font-bold text-blue-600">•</span>
                                      <span className="text-sm">{solution}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="border-t border-gray-200 p-4 bg-gray-50">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">
                {isRunning ? 'Scanning system...' : errorCount > 0 || warningCount > 0 ? 'Fix issues to ensure the best experience' : 'All systems operational!'}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setIsRunning(true);
                    runDiagnostics();
                  }}
                  disabled={isRunning}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition font-medium disabled:bg-blue-400"
                >
                  {isRunning ? 'Running...' : 'Re-run Diagnostics'}
                </button>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="bg-gray-800 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TroubleshootingWrapper;